import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FormInputText } from 'src/components/form';
import {
  convertTimeRangeToTs,
  isRelativeTimeRange,
} from 'src/features/standardDesign/components/DateTimeRangePicker';
import { setSnackbar } from 'src/slices/components';
import { useDispatch } from 'src/store';
import { z } from 'zod';
import CREATE_REPORT_TEMPLATE from '../../graphql/mutation/createReportTemplate';
import GET_REPORT_TEMPLATES from '../../graphql/queries/getReportTemplates';
import { IFormSubmitReportCreation } from './FormFirstStepReportCreation';
import { MetricUnit } from '@/__generated__/graphql';

interface IForm {
  name: string;
  description: string;
}

const formSchema = z.object({
  name: z.string().min(1, t`El nombre de la plantilla no puede estar vacío`),
  description: z.string().nullable(),
});

function getInputMutation(formReportCreation: IFormSubmitReportCreation, data: IForm) {
  const {
    emails,
    createExcel,
    createPdf,
    timezone,
    date,
    request: { deviceIds, metrics },
    meanWindow,
  } = formReportCreation;

  let relativeDate: string | null = null;
  let startTs: number | null = null;
  let stopTs: number | null = null;

  const isRelative = isRelativeTimeRange(date);
  if (isRelative) {
    relativeDate = date.start;
  } else {
    const { start, stop } = convertTimeRangeToTs(date);
    startTs = start;
    stopTs = stop;
  }

  return {
    name: data.name,
    emails: emails,
    timezone: timezone,
    deviceIds: deviceIds,
    metrics: metrics,
    createPdf: createPdf,
    createExcel: createExcel,
    startTs: startTs,
    stopTs: stopTs,
    relativeDate: relativeDate,
    meanWindow: meanWindow,
    description: data.description,
  };
}

const CreateReportTemplateDialog = (props: {
  open: boolean;
  formReportCreation: IFormSubmitReportCreation | undefined;
  onClose: () => void;
}) => {
  const { open, onClose, formReportCreation } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { _ } = useLingui();

  const [mutCreateReportTemplate, { loading }] = useMutation(CREATE_REPORT_TEMPLATE, {
    refetchQueries: [{ query: GET_REPORT_TEMPLATES }],
    onCompleted: () => {
      onClose();
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`La plantilla ha sido creada correctamente`),
          severity: 'success',
        }),
      );
      navigate('/reports');
    },
    onError: (error) => {
      console.error(error);
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`Error al crear la plantilla`),
          severity: 'error',
        }),
      );
    },
  });
  const { control, handleSubmit, formState } = useForm<IForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const onSubmit =
    (execute: boolean): SubmitHandler<IForm> =>
    (data) => {
      if (!formReportCreation) {
        console.error('confirmDialogParams is undefined');
        return;
      }
      const input = getInputMutation(formReportCreation, data);
      if (!input.relativeDate) {
        console.error('Relative date not specified');
        return;
      }

      mutCreateReportTemplate({
        variables: {
          input: {
            createPdf: input.createPdf,
            createExcel: input.createExcel,
            devices: input.deviceIds,
            emails: input.emails,
            metrics: input.metrics.map((metric) => ({
              name: metric.metricName,
              unit: metric.unit as MetricUnit,
            })),
            timezone: input.timezone,
            meanWindow: input.meanWindow,
            name: input.name,
            description: input.description || null,
            relativeTime: input.relativeDate,
          },
        },
      });
    };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
          <Typography variant="h6">
            <Trans>Crear Plantilla</Trans>
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormInputText name="name" control={control} label={_(msg`Nombre de plantilla`)} />
          <FormInputText
            name="description"
            control={control}
            multiline
            minRows={3}
            label={_(msg`Descripción`)}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button color="info" variant="outlined" onClick={onClose}>
          <Trans>Cancelar</Trans>
        </Button>
        <Box flex={1} />
        <Button
          disabled={!formState.isValid || loading}
          color="success"
          variant="outlined"
          onClick={handleSubmit(onSubmit(false))}
        >
          <Trans>Guardar</Trans>
        </Button>
        <Button
          disabled={!formState.isValid || loading}
          color="success"
          variant="contained"
          onClick={handleSubmit(onSubmit(true))}
        >
          <Trans>Guardar y Crear Reporte</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export { CreateReportTemplateDialog };
